import axios from "axios";

export interface VerificationProgress {
  employeeId: string;
  token: string;
  channel?: string;
  status: string;
  steps: {
    email: { status: string; completedAt: string | null };
    phone: { status: string; completedAt: string | null };
    id: { status: string; completedAt: string | null };
    rtw: { status: string; completedAt: string | null };
  };
  canProceed: boolean;
  currentStep: number;
  isUKNative?: boolean;
  ukNativeType?: string;
  maxSteps?: number;
  employee?: {
    fullName: string;
    email: string;
    phoneE164: string;
    inviteChannelPref: string;
  };
}

export interface EmailVerificationRequest {
  token: string;
  email: string;
}

export interface OtpRequest {
  employeeId: string;
  channel: "sms" | "whatsapp" | "email";
  phoneNumber?: string;
}

export interface OtpVerify {
  employeeId: string;
  code: string;
  type?: "phone" | "email";
}

export interface UpdatePhoneRequest {
  employeeId: string;
  phoneNumber: string;
}

export interface UpdateEmailRequest {
  employeeId: string;
  email: string;
}

export interface IdSessionRequest {
  employeeId: string;
}

export interface RtwRequest {
  employeeId: string;
}

export interface CompleteRequest {
  employeeId: string;
}

class VerificationService {
  private baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

  async getProgress(
    token: string,
    channel?: string
  ): Promise<VerificationProgress> {
    try {
      const url = channel
        ? `${this.baseURL}/flow/progress/${token}?channel=${channel}`
        : `${this.baseURL}/flow/progress/${token}`;
      const response = await axios.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to get verification progress"
      );
    }
  }

  async verifyEmail(data: EmailVerificationRequest) {
    try {
      const response = await axios.post(
        `${this.baseURL}/flow/email/verify`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to verify email"
      );
    }
  }

  async requestOtp(data: OtpRequest) {
    try {
      const response = await axios.post(
        `${this.baseURL}/flow/otp/request`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to request OTP");
    }
  }

  async verifyOtp(data: OtpVerify) {
    try {
      const response = await axios.post(
        `${this.baseURL}/flow/otp/verify`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to verify OTP");
    }
  }

  async createIdSession(data: IdSessionRequest) {
    try {
      const response = await axios.post(
        `${this.baseURL}/flow/id/session`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to create ID session"
      );
    }
  }

  async checkRtw(data: RtwRequest) {
    try {
      const response = await axios.post(`${this.baseURL}/flow/rtw/check`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to check RTW");
    }
  }

  async verifyUKWorkPermit(data: {
    employeeId: string;
    code: string;
    forename: string;
    surname: string;
    dob: string;
  }) {
    try {
      const response = await axios.post(
        `${this.baseURL}/flow/rtw/verify`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to verify UK work permit"
      );
    }
  }

  async updatePhoneNumber(data: UpdatePhoneRequest) {
    try {
      const response = await axios.post(
        `${this.baseURL}/flow/phone/update`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to update phone number"
      );
    }
  }

  async updateEmail(data: UpdateEmailRequest) {
    try {
      const response = await axios.post(
        `${this.baseURL}/flow/email/update`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to update email"
      );
    }
  }

  async setUKNative(token: string, ukNativeType: string | null) {
    try {
      const response = await axios.post(`${this.baseURL}/flow/uk-native/set`, {
        token,
        ukNativeType: ukNativeType || undefined, // Send undefined instead of null
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to set UK Native status"
      );
    }
  }

  async complete(data: CompleteRequest) {
    try {
      const response = await axios.post(`${this.baseURL}/flow/complete`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to complete verification"
      );
    }
  }
}

const verificationService = new VerificationService();
export default verificationService;
